import { MyProductColumnGroupId } from './myProductColumnGroups'
import { MyProductColumnId, MyWbProductColumnId, selectColumnId } from './myProductColumnIds'
import { createColumnGroup, createColumns, GroupedMyColumnsParam, MyColumnParam } from './columnGroupCreator'

import { TableDateCell } from '../components/tableCells/TableDateCell'
import { TableSelectCell } from '../components/tableCells/TableSelectCell'
import { ProductImageCell2, UrlImageCell } from '../components/ProductImageCell'
import { MyProductWbSyncCell } from '../components/tableCells/MyProductWbSyncCell'
import { WbProductSyncLogsCell } from '../components/tableCells/StoreProductSyncLogsCell'

import { tableCellStyles } from '../hooks/tableStylesHook'

import { formatBalance, formatPrice, formatPriceOrEmpty } from '../common/numberUtils'

import { MyProductModel } from '../server/mpsklad_core/Models/MyProductModel'
import { WbProductModel } from '../server/mpsklad_core/Models/WbProductModel'
import { CurrencyISO4217 } from '../server/mpsklad_core/Entity/CurrencyISO4217'
import { WbPrintOrderModel } from '../server/mpsklad_core/Models/WbPrintOrderModel'

export const myWbColumns =
  createColumnGroup<MyProductColumnId, MyProductModel>()(
    MyProductColumnGroupId.Wildberries, [{
      title: 'Передача остатков',
      id: 'wbSyncEnabled',
      sortable: true,
      CellComponent: MyProductWbSyncCell
    }, {
      title: 'Код размера (chrtId)',
      id: 'wbChrtId',
      sortable: true,
      accessor: _ => _.wbProducts.map(p => p.chrtId)
    }, {
      title: 'Цена',
      id: 'wbPrice',
      accessor: _ => _.wbProducts.map(p => formatPriceOrEmpty(p.price)),
      cellStyle: tableCellStyles.noWrap
    }, {
      title: 'Цена без скидки',
      id: 'wbBasePrice',
      accessor: _ => _.wbProducts.map(p => formatPriceOrEmpty(p.basePrice)),
      cellStyle: tableCellStyles.noWrap
    }, {
      title: 'Остаток',
      id: 'wbBalance',
      accessor: _ => _.wbProducts.map(p => formatBalance(p.balance)),
      cellStyle: tableCellStyles.noWrap
    }, {
      title: 'В корзине',
      id: 'wbIsTrashed',
      accessor: _ => _.wbProducts.map(p => p.isTrashed ? 'Да' : 'Нет'),
      cellStyle: _ => _.wbProducts.some(p => p.isTrashed) ? tableCellStyles.warning : undefined
    }] satisfies GroupedMyColumnsParam<MyWbProductColumnId, MyProductModel>) // Ensure filtering of ids

export type WbProductColumnId =
  keyof Pick<WbProductModel, 'imageUrl' | 'nmId' | 'chrtId' | 'barcodes' | 'price' | 'basePrice' | 'balance'
    | 'type' | 'brand' | 'name' | 'color' | 'size' | 'supplierArticle' | 'isTrashed' | 'hasMsProduct'>
  | 'logs' | typeof selectColumnId

const wbProductCommonColumns = [{
  id: 'imageUrl',
  title: 'Фото',
  CellComponent: ProductImageCell2
}, {
  id: 'nmId',
  title: 'Код WB'
}, {
  id: 'chrtId',
  title: 'Код размера (chrtId)'
}, {
  id: 'barcodes',
  title: 'Штрихкод',
  cellStyle: tableCellStyles.noWrap
}, {
  id: 'price',
  title: 'Цена',
  accessor: _ => formatPriceOrEmpty(_.price),
  cellStyle: tableCellStyles.noWrap
}, {
  id: 'basePrice',
  title: 'Цена без скидки',
  accessor: _ => formatPriceOrEmpty(_.basePrice),
  cellStyle: tableCellStyles.noWrap
}, {
  id: 'balance',
  title: 'Остаток',
  accessor: _ => formatBalance(_.balance),
  cellStyle: tableCellStyles.noWrap
}, {
  id: 'type',
  title: 'Вид товара'
}, {
  id: 'brand',
  title: 'Бренд'
}, {
  id: 'name',
  title: 'Наименование'
}, {
  id: 'color',
  title: 'Цвет'
}, {
  id: 'size',
  title: 'Размер'
}, {
  id: 'supplierArticle',
  title: 'Артикул поставщика'
}, {
  id: 'isTrashed',
  title: 'В корзине',
  accessor: _ => _.isTrashed ? 'Да' : 'Нет',
  cellStyle: _ => _.isTrashed ? tableCellStyles.warning : undefined
}, {
  id: 'hasMsProduct',
  title: 'Сматчен с МоимСкладом'
}] satisfies Array<MyColumnParam<WbProductColumnId, WbProductModel>>

export const wbProductColumns =
  createColumns<WbProductColumnId, WbProductModel>(
    ...wbProductCommonColumns, {
      id: 'logs',
      title: 'Логи',
      CellComponent: WbProductSyncLogsCell
    })

export const wbProductImportColumns =
  createColumns<WbProductColumnId, WbProductModel>({
    id: selectColumnId,
    title: 'Выбрать',
    CellComponent: TableSelectCell
  }, ...wbProductCommonColumns)

export type WbPrintOrderColumnId =
  keyof Pick<WbPrintOrderModel, 'wbId' | 'imageUrl' | 'chrtId' | 'price' | 'currency' | 'name'
    | 'color' | 'size' | 'wbRId' | 'createdAt' | 'barcode'> | 'productsNames'

export const wbPrintOrderColumns =
  createColumns<WbPrintOrderColumnId, WbPrintOrderModel>({
    id: 'wbId',
    title: '№ задания'
  }, {
    id: 'imageUrl',
    title: 'Фото',
    CellComponent: UrlImageCell
  }, {
    id: 'createdAt',
    title: 'Дата создания',
    CellComponent: TableDateCell
  }, {
    id: 'name',
    title: 'Наименование'
  }, {
    id: 'size',
    title: 'Размер'
  }, {
    id: 'color',
    title: 'Цвет'
  }, {
    id: 'chrtId',
    title: 'Код размера (chrtId)'
  }, {
    id: 'wbRId',
    title: 'Идентификатор вещи'
  }, {
    id: 'barcode',
    title: 'Штрихкод товара',
    cellStyle: tableCellStyles.noWrap
  }, {
    id: 'price',
    title: 'Цена продажи',
    accessor: _ => formatPrice(_.price),
    cellStyle: tableCellStyles.noWrap
  }, {
    id: 'currency',
    title: 'Валюта заказа',
    accessor: _ => _.currency ? CurrencyISO4217[_.currency] : ''
  }, {
    id: 'productsNames',
    title: 'Наименования товаров',
    accessor: _ => _.name
  })